/**
 * Resources — static content stored in code (not a database).
 *
 * Each live resource has its own server-rendered page at
 * `/resources/<slug>`. The resources hub (`/resources`) and the individual
 * resource pages both read from this module. Add more cards here and they
 * will appear on the hub automatically.
 *
 * `status`:
 *   - "live": shown in the "Available now" section, clickable, links to its
 *     own page.
 *   - "soon": shown as a locked "Coming soon" tile under its category. No
 *     page, no sitemap/llms.txt entry. Move an entry to "live" (and give it
 *     a slug + its own route page) to publish it.
 */
export interface Resource {
  /** URL slug for the live resource's own page (required when status is "live"). */
  slug?: string;
  title: string;
  /** Small label above the title (kept for the live card). */
  eyebrow?: string;
  summary: string;
  /** Short label, e.g. "2-page PDF" (live cards only). */
  label?: string;
  /** Number of pages (for the file itself). */
  pages?: number;
  /** Direct download URL for the file (used on the thank-you page). */
  fileUrl?: string;
  /** The webhook page identifier sent with the form submission. */
  webhookPage?: string;
  /** The webhook URL this resource's form posts to (each live resource has its own). */
  webhookUrl?: string;
  status: "live" | "soon";
  /** Type label shown top right of the card, e.g. "Template", "Worksheet". */
  type: string;
  /** Category used to group the "Coming soon" tiles. */
  category: string;
}

/** Display order for the "Coming soon" category headings. */
export const RESOURCE_CATEGORIES = [
  "Know your market",
  "Marketing foundations",
  "Win more customers",
  "Plan your time",
  "Your team",
] as const;

export const resources: Resource[] = [
  {
    slug: "competitor-analysis",
    title: "Competitor Analysis Table",
    eyebrow: "Know your market",
    summary:
      "A side by side look at you and your three closest competitors, on the things customers actually compare before they buy.",
    label: "2-page PDF",
    pages: 2,
    fileUrl:
      "https://assets.cdn.filesafe.space/7dMQRXhpZNOMrhXK0eQA/media/6ade50ba36aa4a07e8c529c4.pdf",
    webhookPage: "Competitor Analysis Template",
    webhookUrl:
      "https://services.leadconnectorhq.com/hooks/7dMQRXhpZNOMrhXK0eQA/webhook-trigger/f50daf3a-5d63-4cc3-8d3b-951f289e1a4f",
    status: "live",
    type: "Template",
    category: "Know your market",
  },

  // --- Know your market ---
  {
    slug: "customer-avatar",
    title: "Customer Avatar Worksheet",
    eyebrow: "Know your market",
    summary:
      "Get clear on who your best customer is, so your marketing speaks to them.",
    label: "7-page PDF",
    pages: 7,
    fileUrl:
      "https://assets.cdn.filesafe.space/7dMQRXhpZNOMrhXK0eQA/media/6ade5dd0d542268bb425da78.pdf",
    webhookPage: "Customer Avatar Worksheet",
    webhookUrl:
      "https://services.leadconnectorhq.com/hooks/7dMQRXhpZNOMrhXK0eQA/webhook-trigger/GfgYLYfEUNLcxz7tzH8n",
    status: "live",
    type: "Worksheet",
    category: "Know your market",
  },
  {
    title: "Building Your Dream List",
    summary:
      "List the customers you would most like to win, and plan how to reach them.",
    status: "soon",
    type: "Worksheet",
    category: "Know your market",
  },
  {
    slug: "compelling-offer",
    title: "Compelling Offer Worksheet",
    eyebrow: "Win more customers",
    summary:
      "Build an offer your customers cannot ignore or compare, so price stops being the only thing they look at.",
    label: "6-page PDF",
    pages: 6,
    fileUrl:
      "https://assets.cdn.filesafe.space/7dMQRXhpZNOMrhXK0eQA/media/6ade5f31736386d0dcc87bb9.pdf",
    webhookPage: "Compelling Offer Worksheet",
    webhookUrl:
      "https://services.leadconnectorhq.com/hooks/7dMQRXhpZNOMrhXK0eQA/webhook-trigger/fSnVtwKqbvnu57ID7ygl",
    status: "live",
    type: "Worksheet",
    category: "Know your market",
  },
  {
    title: "Marketing Pillars",
    summary: "The core building blocks your marketing should stand on.",
    status: "soon",
    type: "Guide",
    category: "Know your market",
  },

  // --- Marketing foundations ---
  {
    title: "Marketing Foundations Checklist",
    summary: "Ten quick checks to see if your marketing basics are in place.",
    status: "soon",
    type: "Checklist",
    category: "Marketing foundations",
  },
  {
    title: "Marketing Quality Test",
    summary: "Find out if your marketing is good enough to bring in the work.",
    status: "soon",
    type: "Checklist",
    category: "Marketing foundations",
  },
  {
    title: "Google Business Profile",
    summary: "Make the most of your free Google listing.",
    status: "soon",
    type: "Checklist",
    category: "Marketing foundations",
  },
  {
    title: "Follow-up Process",
    summary: "Stop leads going cold with a simple follow-up routine.",
    status: "soon",
    type: "Checklist",
    category: "Marketing foundations",
  },
  {
    title: "Price Review",
    summary: "Check whether your prices are where they should be.",
    status: "soon",
    type: "Checklist",
    category: "Marketing foundations",
  },
  {
    title: "Review Collection",
    summary: "A simple way to ask for and collect more reviews.",
    status: "soon",
    type: "Checklist",
    category: "Marketing foundations",
  },
  {
    title: "Marketing Calendar",
    summary: "Plan your marketing for the year in one place.",
    status: "soon",
    type: "Planner",
    category: "Marketing foundations",
  },
  {
    title: "Knowledge Centre",
    summary: "Answer your customers' questions before they ask.",
    status: "soon",
    type: "Checklist",
    category: "Marketing foundations",
  },
  {
    title: "Neighbourhood Cards",
    summary: "Turn every job into new local enquiries.",
    status: "soon",
    type: "Checklist",
    category: "Marketing foundations",
  },
  {
    title: "LinkedIn Profile",
    summary: "Get your LinkedIn profile working for you.",
    status: "soon",
    type: "Checklist",
    category: "Marketing foundations",
  },
  {
    title: "Answer Your Phone",
    summary: "Stop missed calls costing you work.",
    status: "soon",
    type: "Checklist",
    category: "Marketing foundations",
  },

  // --- Win more customers ---
  {
    title: "Emails That Sell",
    summary: "Write emails people read and act on.",
    status: "soon",
    type: "Guide",
    category: "Win more customers",
  },
  {
    title: "The 3 Line Email",
    summary: "A short, simple email that gets replies.",
    status: "soon",
    type: "Template",
    category: "Win more customers",
  },
  {
    title: "Competitions That Win Customers",
    summary: "Run a competition that brings in new customers.",
    status: "soon",
    type: "Guide",
    category: "Win more customers",
  },
  {
    title: "How to Ask for Referrals",
    summary: "Ask for referrals without it feeling awkward.",
    status: "soon",
    type: "Guide",
    category: "Win more customers",
  },
  {
    title: "How to Get More Reviews",
    summary: "Get more five star reviews, more often.",
    status: "soon",
    type: "Guide",
    category: "Win more customers",
  },
  {
    title: "How to Distribute Neighbour Cards",
    summary: "Get your cards through the right doors after every job.",
    status: "soon",
    type: "Guide",
    category: "Win more customers",
  },

  // --- Plan your time ---
  {
    title: "90 Day Plan",
    summary: "Plan your next 90 days on one page.",
    status: "soon",
    type: "Planner",
    category: "Plan your time",
  },
  {
    title: "Weekly Desk Planner",
    summary: "Plan your week at a glance.",
    status: "soon",
    type: "Planner",
    category: "Plan your time",
  },
  {
    title: "Weekly Planner",
    summary: "Block out your week so the important things get done.",
    status: "soon",
    type: "Planner",
    category: "Plan your time",
  },
  {
    title: "Things to Do in Your 90 Minutes",
    summary: "The best ways to use your 90 minutes.",
    status: "soon",
    type: "Guide",
    category: "Plan your time",
  },

  // --- Your team ---
  {
    title: "Recruitment the EC Way",
    summary: "A step by step way to find and hire the right people.",
    status: "soon",
    type: "Guide",
    category: "Your team",
  },
];

export function getResourceBySlug(slug: string): Resource | undefined {
  return resources.find((r) => r.slug === slug);
}
